import { useLocation } from 'react-router-dom'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { usePermissions } from 'lib/rbac/usePermissions'
import { NavLink as NavLinkProps } from 'components/NavigationV3/types'
import { NavLink } from 'components/NavigationV3/components/NavLinks/NavLink'
import { NavSection } from 'components/NavigationV3/components/NavSection'
import {
  IconHome,
  IconCalendarDays,
  IconReceiptBill,
  IconSignature,
  Icon3dPackage2,
  IconArmchair,
  IconSpeedHigh,
  IconBuildings,
  IconBarChartPresentation,
  IconReceiptCredit,
  IconBooks,
  IconRocket,
  Box,
  Flex
} from '@sequencehq/core-components'
import { useWatchtowerNavItem } from 'modules/Automations'
import { isDemoEnv } from 'lib/environment/environment'
import { INVOICES_DEFAULT_SORT_BY } from 'components/Invoices/invoices.constants'

interface NavLinksProps {
  isCollapsed?: boolean
}

export const NavLinks = ({ isCollapsed = false }: NavLinksProps) => {
  const { pathname } = useLocation()
  const flags = useFeatureFlags()
  const { can } = usePermissions()
  const watchtowerNavItem = useWatchtowerNavItem()

  const isDemo = isDemoEnv()

  // Watchtower sits on its own in an untitled, non-collapsible block above the
  // Workspace section.
  const topLinks: NavLinkProps[] = !isDemo ? [watchtowerNavItem] : []

  const workspaceLinks: NavLinkProps[] = [
    {
      icon: IconHome,
      label: 'Home',
      href: '/home',
      'data-testid': 'home-link'
    },
    {
      icon: IconCalendarDays,
      label: 'Schedules',
      href: '/billing-schedules',
      permission: 'org.billing:read',
      'data-testid': 'billing-schedules-link'
    },
    {
      icon: IconBuildings,
      label: 'Customers',
      href: '/customers',
      permission: 'org.customer:read',
      'data-testid': 'customers-link'
    },
    {
      icon: IconReceiptBill,
      label: 'Invoices',
      href: `/invoices?sortBy=${INVOICES_DEFAULT_SORT_BY}`,
      permission: 'org.invoice:read',
      'data-testid': 'invoices-link'
    },
    {
      icon: IconReceiptCredit,
      label: 'Credit notes',
      href: '/credit-notes',
      permission: 'org.creditNote:read',
      'data-testid': 'credit-notes-link'
    },
    {
      icon: IconSignature,
      label: 'Quotes',
      href: '/quotes',
      enabled: flags.showQuoteBuilder,
      permission: 'org.quote:read',
      'data-testid': 'quotes-link'
    },
    {
      icon: Icon3dPackage2,
      label: 'Products',
      href: '/products',
      permission: 'org.product:read',
      'data-testid': 'products-link'
    },
    {
      icon: IconSpeedHigh,
      label: 'Usage',
      href: '/usage-events',
      permission: 'org.usage:read',
      'data-testid': 'usage-link',
      subLinks: [
        {
          label: 'Events',
          href: '/usage-events',
          permission: 'org.usage:read',
          'data-testid': 'usage-events-link'
        },
        {
          label: 'Metrics',
          href: '/usage-metrics',
          permission: 'org.usage:read',
          'data-testid': 'usage-metrics-link'
        }
      ]
    },
    {
      icon: IconArmchair,
      label: 'Seats',
      href: '/seat-events',
      permission: 'org.usage:read', // Seats are part of usage tracking
      'data-testid': 'seats-link',
      subLinks: [
        { label: 'Events', href: '/seat-events', permission: 'org.usage:read' },
        {
          label: 'Balances',
          href: '/seat-balances',
          permission: 'org.usage:read'
        },
        { label: 'Metrics', href: '/seats', permission: 'org.usage:read' }
      ]
    },
    {
      icon: IconBooks,
      label: 'Revenue recognition',
      href: '/revenue-recognition',
      enabled: flags.useRevRec,
      permission: 'org.revenueRecognition:read',
      'data-testid': 'revenue-recognition-link',
      subLinks: [
        {
          label: 'Overview',
          href: '/revenue-recognition/overview',
          permission: 'org.revenueRecognition:read'
        },
        {
          label: 'Revenue waterfall',
          href: '/revenue-recognition/revenue-waterfall',
          permission: 'org.revenueRecognition:read'
        },
        {
          label: 'Journal reports',
          href: '/revenue-recognition/journal-reports',
          permission: 'org.revenueRecognition:read'
        },
        {
          label: 'Deferred revenue',
          href: '/revenue-recognition/deferred-revenue?excludeFullyRecognized=true',
          permission: 'org.revenueRecognition:read'
        }
      ]
    },
    {
      icon: IconBarChartPresentation,
      label: 'Insights',
      href: '/insights'
    },
    {
      icon: IconRocket,
      label: 'Workflows',
      href: '/workflows',
      enabled: !isDemo
    }
  ]

  const navLinkPermissionFilter = (link: NavLinkProps): NavLinkProps | null => {
    if (link.permission && !can(link.permission)) {
      return null
    }

    if (link.subLinks) {
      const filteredSubLinks = link.subLinks
        .map(subLink => {
          if (subLink.permission && !can(subLink.permission)) return null

          const subLinkEnabled = 'enabled' in subLink ? subLink.enabled : true
          return subLinkEnabled ? subLink : null
        })
        .filter(Boolean) as typeof link.subLinks

      // If no subLinks remain after filtering, hide the parent link
      if (filteredSubLinks.length === 0 && link.subLinks.length > 0) return null

      return { ...link, subLinks: filteredSubLinks }
    }

    return link
  }

  const applyPermissionFilter = (links: NavLinkProps[]): NavLinkProps[] =>
    links.map(navLinkPermissionFilter).filter(Boolean) as NavLinkProps[]

  const renderNavLinks = (links: NavLinkProps[]) =>
    links.map(link => (
      <NavLink
        key={link.href}
        {...link}
        pathname={pathname}
        isCollapsed={isCollapsed}
      />
    ))

  const filteredTopLinks = applyPermissionFilter(topLinks)
  const filteredWorkspaceLinks = applyPermissionFilter(workspaceLinks)

  return (
    <Box data-testid="navbar">
      {filteredTopLinks.length > 0 && (
        <Box padding="8px 8px" userSelect="none">
          <Flex direction="column" gap="1px">
            {renderNavLinks(filteredTopLinks)}
          </Flex>
        </Box>
      )}

      <Box padding="8px 8px" userSelect="none">
        <NavSection
          id="workspace"
          title="Workspace"
          isCollapsed={isCollapsed}
          data-testid="navgroup.workspace"
        >
          {renderNavLinks(filteredWorkspaceLinks)}
        </NavSection>
      </Box>
    </Box>
  )
}
